import { createFileRoute, Link } from "@tanstack/react-router";
import { abs } from "@/lib/site";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";

const CONTACT_EMAIL = "contact@coolsymbols.cool";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact" },
      {
        name: "description",
        content:
          "Get in touch with CoolSymbols.cool — send feedback, report a bug, suggest a missing symbol, or ask a privacy or copyright question. We read every email and reply when we can.",
      },
      { property: "og:title", content: "Contact" },
      {
        property: "og:description",
        content: "Feedback, bug reports, missing symbols, privacy or copyright questions — email us.",
      },
      { property: "og:url", content: abs("/contact") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: abs("/contact") }],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-12 sm:px-6 sm:py-16">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">
            Home
          </Link>
          <span aria-hidden="true"> / </span>
          <span className="text-foreground/80">Contact</span>
        </nav>

        <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl">Contact</h1>
        <p className="mt-4 text-lg text-foreground/85">
          CoolSymbols.cool is a small, free project. There are no accounts and no support portal — the
          best (and only) way to reach us is by email, and a real person reads every message.
        </p>

        <div className="mt-8 rounded-xl border border-border bg-surface/40 p-6">
          <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Email us
          </h2>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="mt-2 inline-block break-all font-display text-2xl font-bold text-gradient hover:underline"
          >
            {CONTACT_EMAIL}
          </a>
          <p className="mt-3 text-sm text-muted-foreground">
            We read everything and reply when we can. As a free project we can’t promise a fixed
            response time, so thanks in advance for your patience.
          </p>
        </div>

        <section aria-labelledby="about-heading" className="mt-12">
          <h2 id="about-heading" className="font-display text-xl font-semibold">
            What to write about
          </h2>
          <p className="mt-3 text-foreground/85">A few things people often get in touch about:</p>
          <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <li className="glass rounded-lg px-4 py-3">
              <span className="font-display font-semibold">Feedback &amp; ideas</span>
              <span className="mt-0.5 block text-sm text-muted-foreground">
                A feature you’d like, a tool that would help, or just what you think.
              </span>
            </li>
            <li className="glass rounded-lg px-4 py-3">
              <span className="font-display font-semibold">Bugs &amp; rendering issues</span>
              <span className="mt-0.5 block text-sm text-muted-foreground">
                A symbol that shows as a box, a page that misbehaves — tell us your device and browser.
              </span>
            </li>
            <li className="glass rounded-lg px-4 py-3">
              <span className="font-display font-semibold">Missing or wrong symbols</span>
              <span className="mt-0.5 block text-sm text-muted-foreground">
                A character we’re missing, or one filed in the wrong category.
              </span>
            </li>
            <li className="glass rounded-lg px-4 py-3">
              <span className="font-display font-semibold">Privacy &amp; copyright</span>
              <span className="mt-0.5 block text-sm text-muted-foreground">
                Questions about our{" "}
                <Link to="/privacy" className="text-accent hover:underline">
                  Privacy Policy
                </Link>
                , or a copyright or takedown notice.
              </span>
            </li>
          </ul>
        </section>

        <p className="mt-10 text-sm text-muted-foreground">
          By contacting us you agree to our{" "}
          <Link to="/terms" className="text-accent hover:underline">
            Terms
          </Link>{" "}
          and{" "}
          <Link to="/privacy" className="text-accent hover:underline">
            Privacy Policy
          </Link>
          . We use your email address only to reply to you.
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}
